import { useState, useEffect } from 'react';
import { XMLParser } from 'fast-xml-parser';

const TIMEZONE = 'America/Ciudad_Juarez';

// Funcion de formato de horario
export const formatDate = (dateInput) => {
    if (!dateInput) return '-';

    let date;
    if (typeof dateInput.toDate === 'function') {
        date = dateInput.toDate();
    } else {
        date = new Date(dateInput);
    }

    if (isNaN(date.getTime())) return 'Fecha Inválida';

    return new Intl.DateTimeFormat('es-MX', {
        timeZone: TIMEZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
    }).format(date);
};

/**
 * Formatea Fecha y Hora (para bitácoras y relojes)
 */
export const formatDateTime = (dateInput) => {
    if (!dateInput) return '-';
    
    let date = (typeof dateInput.toDate === 'function') ? dateInput.toDate() : new Date(dateInput);

    return new Intl.DateTimeFormat('es-MX', {
        timeZone: TIMEZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
    }).format(date);
};

/**
 * Calcula días hábiles entre dos fechas, excluyendo fines de semana
 * y una lista personalizada de días festivos.
 * * @param {Date} startDate
 * @param {Date} endDate
 * @param {Array} holidays
 */
export const calculateBusinessDays = (startDate, endDate, holidays = []) => {
    if (!startDate || !endDate) return 0;

    const start = new Date(startDate);
    const end = new Date(endDate);
    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);

    if (end < start) return 0;

    const exactHolidays = new Set();
    const recurringHolidays = new Set();

    holidays.forEach(h => {
        let dateObj;
        
        if (h.fecha && typeof h.fecha.toDate === 'function') {
            dateObj = h.fecha.toDate();
        } else if (h.fecha instanceof Date) {
            dateObj = h.fecha;
        } else if (h.fecha) {
            const str = String(h.fecha).split('T')[0];
            dateObj = new Date(str + 'T12:00:00'); 
        }

        if (dateObj && !isNaN(dateObj.getTime())) {
            const year = dateObj.getFullYear();
            const month = String(dateObj.getMonth() + 1).padStart(2, '0');
            const day = String(dateObj.getDate()).padStart(2, '0');

            const fullDate = `${year}-${month}-${day}`; 
            const monthDay = `${month}-${day}`;         

            if (h.repetirAnualmente) {
                recurringHolidays.add(monthDay);
            } else {
                exactHolidays.add(fullDate);
            }
        }
    });

    let count = 0;
    let current = new Date(start);

    while (current <= end) {
        const dayOfWeek = current.getDay();
        
        const year = current.getFullYear();
        const month = String(current.getMonth() + 1).padStart(2, '0');
        const day = String(current.getDate()).padStart(2, '0');
        
        const currentDateStr = `${year}-${month}-${day}`;
        const currentMonthDay = `${month}-${day}`;

        const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
        
        const isHoliday = exactHolidays.has(currentDateStr) || recurringHolidays.has(currentMonthDay);

        if (!isWeekend && !isHoliday) {
            count++;
        }
        current.setDate(current.getDate() + 1);
    }

    return count;
};

//Funcion para los dias laborales
/**
 * @param {Date} startDate 
 * @param {number} days
 * @return {Date}
 */
export const addBusinessDays = (startDate, days) => {
    if (!startDate) return null;
    let currentDate = new Date(startDate);
    let addedDays = 0;
    while (addedDays < days) {
        currentDate.setDate(currentDate.getDate() + 1);
        const dayOfWeek = currentDate.getDay();
        if (dayOfWeek !== 0 && dayOfWeek !== 6) {
            addedDays++;
        }
    }
    return currentDate;
};


// Funcion para leer los xml en el apartado de finanzas
/**
 * 
 * @param {string} xmlText
 * @return {object|null}
 */
export const parseInvoiceXML = (xmlText) => {
    try {
        const parser = new XMLParser({
            ignoreAttributes: false,
            attributeNamePrefix: "@_",
            parseAttributeValue: true,
        });
        
        const jsonData = parser.parse(xmlText);        
        const comprobante = jsonData["cfdi:Comprobante"];

        if (!comprobante) throw new Error("Nodo <cfdi:Comprobante> no encontrado.");

        const complemento = comprobante["cfdi:Complemento"]?.["tfd:TimbreFiscalDigital"];
        if (!complemento) throw new Error("Nodo <tfd:TimbreFiscalDigital> no encontrado.");

        let iva = 0;
        const impuestosNode = comprobante["cfdi:Impuestos"];
        if (impuestosNode && impuestosNode["@_TotalImpuestosTrasladados"]) {
            iva = impuestosNode["@_TotalImpuestosTrasladados"];
        } else if (impuestosNode && impuestosNode["cfdi:Traslados"]) {
            const trasladosNode = impuestosNode["cfdi:Traslados"];
            const traslados = Array.isArray(trasladosNode["cfdi:Traslado"])
                ? trasladosNode["cfdi:Traslado"]
                : [trasladosNode["cfdi:Traslado"]];
            
            traslados.forEach(t => {
                if (t && t['@_Impuesto'] === '002') {
                    iva += t['@_Importe'];
                }
            });
        }

        return {
            folio: comprobante['@_Folio'] || 'S/F',
            uuid: complemento['@_UUID'],
            subtotal: comprobante['@_SubTotal'],
            iva: iva,
            monto: comprobante['@_Total'],
            fechaEmision: new Date(comprobante['@_Fecha']),
            rfcEmisor: comprobante["cfdi:Emisor"]?.['@_Rfc'],
            rfcReceptor: comprobante["cfdi:Receptor"]?.['@_Rfc'],
        };
    } catch (err) {
        console.error("Error al parsear el XML:", err);
        return null;
    }
};


/**
 * Calcula el saldo de vacaciones incluyendo años completos y la parte proporcional del año en curso.
 */
export const calculateVacationBalance = (fechaIngresoTimestamp, historialVacaciones = []) => {
    if (!fechaIngresoTimestamp) return { disponibles: 0, antiguedad: 0, proporcional: 0 };

    const ingreso = fechaIngresoTimestamp.toDate();
    const hoy = new Date();
    
    ingreso.setHours(0,0,0,0);
    hoy.setHours(0,0,0,0);

    let antiguedad = hoy.getFullYear() - ingreso.getFullYear();
    const m = hoy.getMonth() - ingreso.getMonth();
    if (m < 0 || (m === 0 && hoy.getDate() < ingreso.getDate())) {
        antiguedad--;
    }

    const getDiasLey = (anioServicio, esReforma) => {
        let dias = 0;
        if (esReforma) {
            if (anioServicio === 1) dias = 12;
            else if (anioServicio === 2) dias = 14;
            else if (anioServicio === 3) dias = 16;
            else if (anioServicio === 4) dias = 18;
            else if (anioServicio === 5) dias = 20;
            else if (anioServicio >= 6 && anioServicio <= 10) dias = 22;
            else if (anioServicio >= 11 && anioServicio <= 15) dias = 24;
            else dias = 24 + Math.floor((anioServicio - 11) / 5) * 2;
        } else {
            if (anioServicio === 1) dias = 6;
            else if (anioServicio === 2) dias = 8;
            else if (anioServicio === 3) dias = 10;
            else if (anioServicio === 4) dias = 12;
            else if (anioServicio >= 5 && anioServicio <= 9) dias = 14;
            else dias = 14 + Math.floor((anioServicio - 10) / 5) * 2;
        }
        return dias;
    };

    let diasGanadosCompletos = 0;
    
    for (let i = 1; i <= antiguedad; i++) {
        const anioAniversario = ingreso.getFullYear() + i;
        const esReforma = anioAniversario >= 2023; 
        diasGanadosCompletos += getDiasLey(i, esReforma);
    }

    const anioEnCurso = antiguedad + 1;
    const anioCalendarioActual = hoy.getFullYear();
    const diasCorrespondientesEsteAno = getDiasLey(anioEnCurso, anioCalendarioActual >= 2023);
    const ultimoAniversario = new Date(ingreso);
    ultimoAniversario.setFullYear(ingreso.getFullYear() + antiguedad);
    
    const diferenciaTiempo = hoy.getTime() - ultimoAniversario.getTime();
    const diasTranscurridos = diferenciaTiempo / (1000 * 3600 * 24);
    const diasProporcionales = (diasTranscurridos / 365) * diasCorrespondientesEsteAno;
    const diasTomados = historialVacaciones.reduce((acc, curr) => acc + (curr.dias || 0), 0);
    const totalGanadoHastaHoy = diasGanadosCompletos + diasProporcionales;

    return {
        disponibles: parseFloat((totalGanadoHastaHoy - diasTomados).toFixed(2)),
        antiguedad: antiguedad,
        ganadosCompletos: diasGanadosCompletos,
        proporcionales: parseFloat(diasProporcionales.toFixed(2)),
        tomados: diasTomados
    };
};

// --- CUSTOM HOOKS ---

//countdown
export const useCountdown = (endTime) => {
    const [timeLeft, setTimeLeft] = useState(0);

    useEffect(() => {
        if (!endTime) {
            setTimeLeft(0);
            return;
        }

        const endTimestamp = endTime.toDate().getTime();
        const interval = setInterval(() => {
            const now = Date.now();
            const remaining = endTimestamp - now;
            setTimeLeft(remaining > 0 ? remaining : 0);
        }, 1000);

        return () => clearInterval(interval);
    }, [endTime]);

    const formatTime = (ms) => {
        if (ms <= 0) return "00:00";
        const totalSeconds = Math.floor(ms / 1000);
        const minutes = String(Math.floor(totalSeconds / 60)).padStart(2, '0');
        const seconds = String(totalSeconds % 60).padStart(2, '0');
        return `${minutes}:${seconds}`;
    };

    return formatTime(timeLeft);
};

//logica para cronometrar a los tecnicos
export const useTimer = (startTime) => {
    const [elapsedTime, setElapsedTime] = useState(0);

    useEffect(() => {
        if (!startTime) {
            setElapsedTime(0);
            return;
        }

        const startTimestamp = startTime.toDate().getTime();
        const interval = setInterval(() => {
            const now = Date.now();
            setElapsedTime(now - startTimestamp);
        }, 1000);

        return () => clearInterval(interval);
    }, [startTime]);

    const formatTime = (ms) => {
        const totalSeconds = Math.floor(ms / 1000);
        const hours = String(Math.floor(totalSeconds / 3600)).padStart(2, '0');
        const minutes = String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, '0');
        const seconds = String(totalSeconds % 60).padStart(2, '0');
        return `${hours}:${minutes}:${seconds}`;
    };

    return formatTime(elapsedTime);
};

//logica para las vigencia de los estudios
export const calcularVigencia = (fechaEmision, vigenciaAnos) => {
    if (Number(vigenciaAnos) === 0) {
        return { texto: 'Indefinido', colorClass: 'bg-blue-100 text-blue-800' };
    }
    
    if (!fechaEmision || vigenciaAnos === undefined || vigenciaAnos === null || vigenciaAnos === "") {
        return { texto: '---', colorClass: 'bg-gray-100 text-gray-500' };
    }

    const fechaInicio = typeof fechaEmision.toDate === 'function' ? fechaEmision.toDate() : new Date(fechaEmision);
    
    const fechaVencimiento = new Date(fechaInicio);
    fechaVencimiento.setFullYear(fechaVencimiento.getFullYear() + Number(vigenciaAnos));

    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    fechaVencimiento.setHours(0, 0, 0, 0);

    const diferenciaMs = fechaVencimiento.getTime() - hoy.getTime();
    const diasRestantes = Math.ceil(diferenciaMs / (1000 * 60 * 60 * 24));

    if (diasRestantes < 0) {
        return { texto: 'Expirado', colorClass: 'bg-red-100 text-red-800 font-bold' };
    }
    if (diasRestantes === 0) {
        return { texto: 'Vence hoy', colorClass: 'bg-red-100 text-red-800 font-bold' };
    }

    let textoResult = '';
    if (diasRestantes > 365) {
        const anos = Math.floor(diasRestantes / 365);
        const meses = Math.floor((diasRestantes % 365) / 30);
        textoResult = `${anos} año(s) ${meses > 0 ? `y ${meses} mes(es)` : ''}`;
    } else if (diasRestantes > 30) {
        const meses = Math.floor(diasRestantes / 30);
        const dias = diasRestantes % 30;
        textoResult = `${meses} mes(es) ${dias > 0 ? `y ${dias} día(s)` : ''}`;
    } else {
        textoResult = `${diasRestantes} día(s)`;
    }

    let colorClass = 'bg-green-100 text-green-800';
    if (diasRestantes <= 30) {
        colorClass = 'bg-orange-100 text-orange-800';
    } else if (diasRestantes <= 90) {
        colorClass = 'bg-yellow-100 text-yellow-800';
    }

    return { 
        texto: `Vence en ${textoResult}`, 
        colorClass: colorClass 
    };
};
